import { AfterViewInit, Component, computed, effect, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router } from '@angular/router';
import { Role } from 'src/app/models/user/role.model';
import { ISelect } from '../../components/mat-multiselect/mat-multiselect.component';
import { IProviderOrganizationRelationships } from 'src/app/models/provider/provider-organization-relationships.model';
import { ProviderOrganizationsService } from 'src/app/services/provider.organizations.service';
import { LenderOrganizationsService } from 'src/app/services/lender.organizations.service';
import { ConsultantsService } from 'src/app/services/consultants.service';
import { UsersService } from 'src/app/services/users.service';
import { ToastService } from 'src/app/services/toast.service';
import { BulkUploadDocumentsComponent } from '../../components/bulk-upload-documents/bulk-upload-documents.component';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime, map, skip, tap } from 'rxjs/operators';

@Component({
  selector: 'app-provider-bulk-upload',
  templateUrl: './provider-bulk-upload.component.html',
  styleUrls: ['./provider-bulk-upload.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    BulkUploadDocumentsComponent
  ],
})
export class ProviderBulkUploadComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(BulkUploadDocumentsComponent) uploadComponent!: BulkUploadDocumentsComponent;
  @ViewChild('optionsContainer') optionsProvidersContainer!: ElementRef;

  // Despite the "Providers" naming (kept to avoid touching the working provider
  // load logic), these fields and the paging methods below are shared by both
  // provider and lender modes — see isLenderMode / organizationType.
  providersList: ISelect[] = [];
  isLoading = false;
  documentUploaded = false;
  hasNextPageProviders = true;
  currentPageProviders = 1;
  pageSize = 20;
  private subscriptions: Subscription[] = [];

  // Which entity this screen uploads for, sourced reactively from route data
  // ({ organizationType: 'lender' }). Defaults to 'provider' so the existing
  // provider route/behavior is unchanged. Read as a signal so the load effect
  // depends on it directly, independent of field/constructor init ordering.
  readonly organizationType = toSignal(
    this.route.data.pipe(
      map((data) => (data['organizationType'] === 'lender' ? 'lender' : 'provider') as 'provider' | 'lender')
    ),
    { initialValue: 'provider' as 'provider' | 'lender' }
  );
  readonly isLenderMode = computed(() => this.organizationType() === 'lender');
  get organizationLabel(): string {
    return this.isLenderMode() ? 'Lender' : 'Provider';
  }

  form: FormGroup = new FormGroup({
    organizationId: new FormControl('', { validators: [Validators.required] }),
  });

  readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly organizationId = toSignal(this.userService.organizationId$, { initialValue: undefined });
  readonly isProvider = computed(() => this.userRole() === Role.Provider);
  readonly isConsultant = computed(() => this.userRole() === Role.Consultant);
  readonly isAdminOrSystem = computed(() => {
    const role = this.userRole();
    return role === Role.Admin || role === Role.SystemUser;
  });

  readonly _loadProviderOrganizationsEffect = effect(() => {
    const role = this.userRole();
    const orgId = this.organizationId();
    const isLender = this.isLenderMode(); // tracked: re-runs when route data resolves

    // Lender mode is admin-driven only: load the lender organizations list.
    // (Lender/Consultant self-service branches below apply to provider mode.)
    if (isLender) {
      if (role === Role.Admin || role === Role.SystemUser) {
        this.currentPageProviders = 1;
        this.hasNextPageProviders = true;
        this.lenderService.get({
          page: 1,
          pageSize: this.pageSize,
          sortColumn: 'name',
          sortOrder: 'asc',
          isActive: true
        }).subscribe((response: any) => {
          if (response.isError) {
            this.providersList = [];
            return;
          }
          this.providersList = response.data.items.map((lender: any) => ({
            id: lender.id,
            name: lender.name
          }));
          this.hasNextPageProviders = response.data.hasNextPage;
          this.currentPageProviders = response.data.page;
          this.form.get('organizationId')?.enable();
        });
      }
      return;
    }

    if (role === Role.Provider && orgId) {
      this.providerService.getRelationships(orgId.toString()).subscribe((response) => {
        if (response.isError) {
          this.form.get('organizationId')?.setValue(orgId);
          this.form.get('organizationId')?.disable();
          return;
        }

        const data = response.data as IProviderOrganizationRelationships;

        if (data.parent !== null) {
          this.providersList = [
            { id: data.currentOrganization.id, name: data.currentOrganization.name }
          ];
          this.form.get('organizationId')?.setValue(orgId);
          this.form.get('organizationId')?.disable();
        } else if (data.children.length > 0) {
          this.providersList = [
            { id: data.currentOrganization.id, name: data.currentOrganization.name },
            ...data.children.map(child => ({ id: child.id, name: child.name }))
          ];
          this.form.get('organizationId')?.enable();
          this.form.get('organizationId')?.setValue(orgId);
        } else {
          this.providersList = [
            { id: data.currentOrganization.id, name: data.currentOrganization.name }
          ];
          this.form.get('organizationId')?.setValue(orgId);
          this.form.get('organizationId')?.disable();
        }
      });
    } else if (role === Role.Consultant) {
      this.consultantsService.getCurrentConsultantAssignedOrganizations().subscribe((response) => {
        if (response.isError) {
          this.providersList = [];
          return;
        }
        this.providersList = response.data.items.map((org: any) => ({
          id: org.id,
          name: org.name
        }));
        this.form.get('organizationId')?.enable();
      });
    } else if (role === Role.Admin || role === Role.SystemUser) {
      this.currentPageProviders = 1;
      this.hasNextPageProviders = true;
      this.providerService.get({
        page: 1,
        pageSize: this.pageSize,
        sortColumn: 'name',
        sortOrder: 'asc',
        isActive: true
      }).subscribe((response: any) => {
        if (response.isError) {
          this.providersList = [];
          return;
        }
        this.providersList = response.data.items.map((provider: any) => ({
          id: provider.id,
          name: provider.name
        }));
        this.hasNextPageProviders = response.data.hasNextPage;
        this.currentPageProviders = response.data.page;
        this.form.get('organizationId')?.enable();
      });
    }
  });

  fromFormsManagement = false;
  private returnOrganizationId: string | null = null;

  constructor(
    private providerService: ProviderOrganizationsService,
    private lenderService: LenderOrganizationsService,
    private consultantsService: ConsultantsService,
    private userService: UsersService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // organizationType is derived reactively from route data (see field above).
    this.fromFormsManagement = this.route.snapshot.queryParamMap.get('from') === 'forms-management';
    this.returnOrganizationId = this.route.snapshot.queryParamMap.get('organizationId');
  }

  returnToFormsManagement(): void {
    const userRole = this.userRole();
    let route: string;

    if (userRole === Role.Admin || userRole === Role.SystemUser) {
      route = `/admin/providers-list/${this.returnOrganizationId}`;
    } else {
      route = `/provider/providers-list/${this.returnOrganizationId}`;
    }

    this.router.navigate([route], {
      queryParams: { tab: 'forms-management' }
    });
  }

  ngOnInit(): void {
    this.form.get('organizationId')?.valueChanges
      .pipe(skip(1))
      .subscribe(() => {
        this.clearFilesOnOrganizationChange();
      });
  }

  ngAfterViewInit(): void {
    this._initInfiniteScroll(
      this.optionsProvidersContainer,
      () => this.shouldLoadMoreProviders(),
      () => this.onLoadMoreProviders()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private _initInfiniteScroll(
    elementRef: ElementRef | undefined,
    shouldLoad: () => boolean,
    loadMore: () => void
  ): void {
    if (!elementRef) return;

    const sub = fromEvent(elementRef.nativeElement, 'scroll')
      .pipe(
        debounceTime(300),
        tap(() => {
          if (shouldLoad()) {
            loadMore();
          }
        })
      )
      .subscribe();

    this.subscriptions.push(sub);
  }

  private shouldLoadMoreProviders(): boolean {
    if (!this.hasNextPageProviders) return false;
    const container = this.optionsProvidersContainer.nativeElement;
    return container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
  }

  private onLoadMoreProviders(): void {
    const orgService = this.isLenderMode() ? this.lenderService : this.providerService;
    orgService.get({
      page: this.currentPageProviders + 1,
      pageSize: this.pageSize,
      sortColumn: 'name',
      sortOrder: 'asc',
      isActive: true
    }).subscribe((response: any) => {
      if (response.isError) return;
      const newItems = response.data.items.map((org: any) => ({
        id: org.id,
        name: org.name
      }));
      this.providersList = [...this.providersList, ...newItems];
      this.hasNextPageProviders = response.data.hasNextPage;
      this.currentPageProviders = response.data.page;
      });
  }

  /**
   * Clear all files when organization changes to prevent accidental uploads to wrong org
   */
  private clearFilesOnOrganizationChange(): void {
    if (this.uploadComponent && this.uploadComponent.files.length > 0) {
      const fileCount = this.uploadComponent.files.length;
      this.uploadComponent.clearAll();
      this.toastService.success(
        `Organization changed. Cleared ${fileCount} file(s) from upload queue.`,
        'Files Cleared'
      );
    }
  }

  uploadingEventHandler(event: boolean): void {
    // Update loading state based on upload status
    if (!event) {
      // Uploads complete
      this.isLoading = false;
    }
  }

  onFileRemoveHandler(event: any): void {
    // File count updated
  }

  hasDocumentsUploaded(): boolean {
    // Check if upload component has any files
    return this.uploadComponent?.files?.length > 0;
  }

  /**
   * Check if there are any files that can be uploaded (QUEUED or ERROR status)
   */
  hasUploadableFiles(): boolean {
    if (!this.uploadComponent?.files) {
      return false;
    }
    // Check if there are any files that are not successfully uploaded
    return this.uploadComponent.activeFileCount > 0;
  }

  /**
   * Check if submit button should be disabled
   */
  isSubmitDisabled(): boolean {
    return this.form.invalid || !this.hasUploadableFiles() || this.isLoading;
  }

  onSubmit(): void {
    if (this.isSubmitDisabled()) {
      return;
    }

    if (!this.uploadComponent) {
      console.error('Upload component not found!');
      return;
    }

    this.isLoading = true;

    // Get organization ID from form and pass to upload component
    const organizationId = this.form.get('organizationId')?.value;

    // Start uploading the files with organization context
    this.uploadComponent.startUpload(organizationId);

    // Note: isLoading will be set to false when uploadingEvent emits false
  }

}
